/** @format */
import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  _$,
  Tabs,
  Tab,
  Form,
  FormItem,
  Input,
  Select,
  Option,
  Icon,
  Button,
  Message
} from '@king-design/react'
import styled from 'styled-components'
import MonitorSearch from 'src/components/MonitorSearch'
import {
  InstanceDialogProps,
  GroupItemProps,
  FilterFormState,
  initFilterForm,
  InstanceItem
} from './constants'
import { BatchSearchTextarea, TablePage } from 'src/components'
import { request } from 'src/utils'
import API from 'src/api/ApiCommon'
import RegionBtn from 'src/components/RegionBtn'
import useFormTable from 'src/hooks/useFormTable'
import APICloud from 'src/api/ApiCloud'
import useTableGroup from 'src/hooks/useTableGroup'

export type SearchQueryType =
  | 'all'
  | 'SearchInstanceNames'
  | 'SearchInstances'
  | 'SearchInstanceIPs'

export const searchQueryType = {
  all: '全局搜索',
  SearchInstanceNames: '实例名称',
  SearchInstances: '实例ID',
  SearchInstanceIPs: '实例信息'
}

const handleQuery = (type: SearchQueryType, query: string) => {
  const params: any = {}
  if (type === 'all') {
    params.Query = query || ''
  } else {
    const value = query.split(/[\\;\\；\\,\\，\s\n]+/).filter(item => !!item)
    if (value?.length > 100) {
      return false
    }
    params[type] = value
  }
  return params
}
const InstanceDialog: React.FC<InstanceDialogProps> = ({
  className,
  productType,
  visible,
  region,
  isGroupFilter,
  groupListProps,
  instanceMaxLength,
  isRegionFilter = false,
  batchSearch = false,
  selectedInstance = [],
  setSelectedInstance,
  disabled
}) => {
  const [tabsValue, setTabsValue] = useState<string>('all')
  const [groupList, setGroupList] = useState<GroupItemProps[]>([])
  const [searchType, setSearchType] = useState<SearchQueryType>('all')
  const [formValue, setFormValue] = useState<FilterFormState>({
    ...initFilterForm
  })
  const [checkedKeys, setCheckedKeys] = useState<string[]>([])
  // 临时存储
  const [selectedQuery, setSelectedQuery] = useState<string>('')
  const [tableData, setTableData] = useState<InstanceItem[]>([])
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const regionGroup = useTableGroup('monitorRegionList')
  const [selectedGroup, setSelectedGroup] = useState<{ region: string }>({ region: '' })
  const formRef = useRef(null)
  useEffect(() => {
    if (isGroupFilter) {
      if (Array.isArray(groupListProps)) {
        setGroupList(groupListProps)
      } else {
        getGroupList()
      }
    }
  }, [isGroupFilter])

  const { run, result, pageState, loading, total } = useFormTable(
    APICloud.getInstanceList,
    {
      manual: true,
      method: searchType === 'all' ? 'GET' : 'POST',
      params: {
        ProductType: parseInt(productType),
        GroupId: isNaN(parseInt(formValue.group)) ? undefined : parseInt(formValue.group),
        RegionKey: region || formValue.region,
        InstanceType: 1,
        PageSize: 100,
        ...handleQuery(searchType, formValue.filterValue)
      }
    },
    data => {
      return {
        tableData: data?.resourceList || [],
        totalCount: data?.totalCount
      }
    }
  )

  useEffect(() => {
    setTableData(result || [])
  }, [result])

  // 筛选后修改表格
  const selectedTable = useMemo(() => {
    const region = selectedGroup.region
    if (region || selectedQuery) {
      const regionFilter = selectedInstance.filter(instance => {
        return region ? instance.region === region : true
      })
      const queryFilter = regionFilter.filter(({ instanceName, instanceIP, instanceId }) => {
        return selectedQuery
          ? instanceName.toLocaleLowerCase().includes(selectedQuery.toLocaleLowerCase()) ||
              instanceIP.toLocaleLowerCase().includes(selectedQuery.toLocaleLowerCase()) ||
              instanceId.toLocaleLowerCase().includes(selectedQuery.toLocaleLowerCase())
          : true
      })
      return queryFilter
    } else {
      return selectedInstance
    }
  }, [selectedInstance, selectedGroup.region, selectedQuery])

  const addInstances = () => {
    const update = checkedKeys.map(instanceId => {
      const find = tableData.find(item => item.instanceId === instanceId)
      return find
    })
    const filter = update.filter(item => {
      const id = item?.instanceId
      if (!id) {
        return false
      } else {
        const find = selectedInstance.find(({ instanceId }) => instanceId === id)
        return !find
      }
    })
    if (instanceMaxLength && selectedInstance?.length + filter?.length > instanceMaxLength) {
      Message.error(_$('实例最多选择', { num: instanceMaxLength }))
      return
    } else {
      setSelectedInstance(selectedInstance.concat(filter))
    }
  }

  const allScheme = {
    instanceName: {
      title: _$('实例名称')
    },
    instanceId: {
      title: _$('实例ID')
    },
    instanceIP: {
      title: _$('IP地址'),
      width: 150
    },
    active: {
      title: _$('操作'),
      width: 150,
      template(data) {
        const find = selectedInstance.find(item => item.instanceId === data.instanceId)
        return !find ? (
          <a
            className="primary-color"
            onClick={() => {
              if (instanceMaxLength && selectedInstance?.length + 1 > instanceMaxLength) {
                Message.error(_$('实例最多选择', { num: instanceMaxLength }))
                return
              } else {
                setSelectedInstance([
                  ...selectedInstance,
                  {
                    ...data
                  }
                ])
              }
            }}
          >
            {_$('添加')}
          </a>
        ) : (
          <span style={{ color: '#999' }}>{_$('已添加')}</span>
        )
      }
    }
  }

  const selectedScheme = {
    instanceName: {
      title: _$('实例名称')
    },
    instanceIP: _$('IP地址'),
    region: {
      title: _$('region'),
      group: region ? undefined : regionGroup,
      template(item) {
        const find = regionGroup.find(({ value }) => item.region === value)
        return find?.label || item.region
      }
    },
    action: {
      title: _$('操作'),
      width: 150,
      template({ instanceId }) {
        if (disabled) {
          return ''
        }
        return (
          <a
            className="primary-color"
            onClick={() => {
              unbindInstances([instanceId])
            }}
          >
            {_$('解绑')}
          </a>
        )
      }
    }
  }

  const unbindInstances = (keys: string[]) => {
    const update = selectedInstance.filter(item => !keys.includes(item.instanceId))
    setSelectedInstance([...update])
  }

  // 获取实例组List
  const getGroupList = async () => {
    const { data } = await request(API.getGroupList, {
      data: { PageIndex: 1, pageSize: 999 }
    })
    if (data?.groupList?.length) {
      setGroupList(data?.groupList)
    }
  }

  useEffect(() => {
    if (disabled) {
      setTabsValue('selected')
    }
  }, [disabled])

  useEffect(() => {
    if (visible) {
      setTabsValue('all')
      run({ PageIndex: 1, PageSize: 100 })
    } else {
      setFormValue({ ...initFilterForm, region: localStorage.getItem('systemId') })
      setSearchType('all')
    }
  }, [visible])

  return (
    <div className={className}>
      {!disabled ? (
        <Tabs
          value={tabsValue}
          onChangeValue={tab => {
            setTabsValue(tab)
          }}
        >
          <Tab value="all">{_$('全部实例')}</Tab>
          <Tab value="selected">
            {_$('已选实例')}(<span className="primary-color">{selectedInstance?.length || 0}</span>)
          </Tab>
        </Tabs>
      ) : null}
      {tabsValue === 'all' ? (
        <div className="tabs-all">
          <div className="fliter">
            {isRegionFilter ? (
              <RegionBtn
                defaultValue={region || formValue.region}
                isUpdate={false}
                regionChange={value => {
                  setFormValue({
                    ...formValue,
                    region: value
                  })
                  run({
                    RegionKey: value,
                    PageIndex: 1
                  })
                }}
              />
            ) : null}
            <Form ref={formRef} layout="inline" starOnRequired={true}>
              <FormItem hideLabel={true}>
                <Button type="primary" disabled={!checkedKeys?.length} onClick={addInstances}>
                  {_$('批量添加')}
                </Button>
              </FormItem>
              {isGroupFilter ? (
                <FormItem hideLabel={true} value={formValue.group}>
                  <Select
                    width="200"
                    filterable
                    value={formValue.group}
                    placeholder={_$('请选择资源分组')}
                    clearable={true}
                    onChangeValue={value => {
                      setFormValue({
                        ...formValue,
                        group: value
                      })
                    }}
                  >
                    {groupList?.length
                      ? groupList.map(({ groupId, groupName }) => (
                          <Option key={groupId} value={groupId}>
                            {groupName}
                          </Option>
                        ))
                      : null}
                  </Select>
                </FormItem>
              ) : null}
              {batchSearch ? (
                <FormItem className="search_type_item" hideLabel={true} value={searchType}>
                  <Select
                    width="120"
                    value={searchType}
                    onChangeValue={value => {
                      setSearchType(value)
                    }}
                  >
                    {Object.keys(searchQueryType).map(key => {
                      const name = searchQueryType[key]
                      return (
                        <Option key={key} value={key}>
                          {_$(name)}
                        </Option>
                      )
                    })}
                  </Select>
                </FormItem>
              ) : null}
              <FormItem hideLabel={true} value={formValue.filterValue}>
                {searchType !== 'all' ? (
                  <BatchSearchTextarea
                    placeholder={_$('多个资源精确搜索，支持回车、空格、分号、逗号分隔符')}
                    value={formValue.filterValue}
                    onChange={value => {
                      setFormValue({
                        ...formValue,
                        filterValue: value
                      })
                    }}
                  />
                ) : (
                  <Input
                    placeholder={_$('单个资源模糊搜索')}
                    value={formValue.filterValue}
                    onChangeValue={value => {
                      setFormValue({
                        ...formValue,
                        filterValue: value
                      })
                    }}
                  />
                )}
              </FormItem>
              <FormItem hideLabel={true}>
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={() => {
                    const params: any = {
                      PageIndex: 1,
                      GroupId: formValue.group || undefined
                    }
                    const query = handleQuery(searchType, formValue.filterValue)
                    if (query) {
                      run({ ...params, ...query })
                    } else {
                      Message.error(_$('输入查询值超过100个，请减少查询值个数后重试'))
                    }
                  }}
                >
                  <Icon className="ion-ios-search" />
                  {_$('查询')}
                </Button>
              </FormItem>
            </Form>
            <TablePage
              tableProps={{
                data: tableData,
                scheme: allScheme,
                resizable: true,
                rowKey: item => item.instanceId,
                onChangeCheckedKeys: keys => {
                  setCheckedKeys(keys)
                },
                checkedKeys,
                rowCheckable: false,
                disableRow: ({ instanceId }) => {
                  const find = selectedInstance.find(item => item.instanceId === instanceId)
                  return !!find
                }
              }}
              pageProps={{
                limits: [10, 20, 50, 100],
                total,
                ...pageState
              }}
              loading={loading}
              run={run}
            />
          </div>
        </div>
      ) : (
        ''
      )}
      {tabsValue === 'selected' ? (
        <div className="tabs-selected">
          {!disabled ? (
            <Button
              type="primary"
              disabled={!selectedKeys.length}
              onClick={() => {
                unbindInstances(selectedKeys)
                setSelectedKeys([])
              }}
            >
              {_$('批量解绑')}
            </Button>
          ) : null}
          <MonitorSearch
            placeholder={_$('输入实例ID、实例名称、实例信息')}
            value={selectedQuery}
            onChange={val => {
              setSelectedQuery(val)
            }}
          />
          <TablePage
            tableProps={{
              data: selectedTable,
              scheme: selectedScheme,
              resizable: true,
              rowCheckable: false,
              rowKey: item => item.instanceId,
              checkedKeys: selectedKeys,
              onChangeCheckedKeys: item => {
                setSelectedKeys(item)
              },
              group: selectedGroup,
              onChangeGroup: value => {
                setSelectedGroup(value)
              }
            }}
          />
        </div>
      ) : (
        ''
      )}
    </div>
  )
}

export default styled(InstanceDialog)`
  .tabs-all {
    margin-top: 16px;
    .k-form {
      margin-top: 8px;
      .k-form-item {
        margin-bottom: 0;
        margin-right: 8px;
        .search_type_item {
          margin-right: 0;
          .k-select .k-wrapper {
            border-right: none;
          }
        }
      }
    }
    .table-page {
      margin-top: 0;
    }
  }
  .tabs-selected {
    margin-top: 16px;
    .k-btn {
      margin-right: 16px;
    }
    .k-table {
      margin-top: 16px;
    }
  }
`
